(() => {
  const phone = window.matchMedia('(max-width: 767px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initGallery = (root) => {
    const gallery = root.querySelector('[data-mp-gallery]');
    const track = gallery?.querySelector('[data-mp-gallery-track]');
    const items = [...(track?.querySelectorAll('[data-mp-media-id]') || [])];
    if (!track || items.length < 2 || gallery.dataset.mpMobileGalleryBound) return;
    gallery.dataset.mpMobileGalleryBound = 'true';
    let current = items.findIndex(item => item.getAttribute('aria-hidden') !== 'true' && !item.hidden);
    if (current < 0) current = 0;
    let wrapTarget = null;
    let wrapTimer;
    let drag;
    const clones = [];
    const hydrateImage = item => {
      const template = item?.querySelector('template[data-mp-media-image]');
      if (!template) return;
      template.content.querySelector('img')?.setAttribute('loading', 'eager');
      template.replaceWith(template.content);
    };
    const warmNextImage = () => {
      const index = current;
      const warm = () => {
        if (phone.matches && current === index) hydrateImage(items[(index + 1) % items.length]);
      };
      const idle = () => {
        if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 1500 });
        else requestAnimationFrame(warm);
      };
      const image = items[index].querySelector('img');
      if (!image || image.complete) idle();
      else image.addEventListener('load', idle, { once: true });
    };
    const instant = (index) => {
      gallery.dataset.mpGalleryInstant = 'true';
      track.style.setProperty('--mp-gallery-index', String(index));
      track.getBoundingClientRect();
      requestAnimationFrame(() => delete gallery.dataset.mpGalleryInstant);
    };
    const finishWrap = () => {
      window.clearTimeout(wrapTimer);
      if (wrapTarget === null) return;
      instant(wrapTarget);
      wrapTarget = null;
    };
    track.addEventListener('transitionend', event => {
      if (event.target === track && event.propertyName === 'transform') finishWrap();
    });
    const revealDot = () => {
      const dot = gallery.querySelector('.mp-gallery__dot.is-active');
      const strip = dot?.parentElement;
      if (!dot || !strip) return;
      const left = dot.offsetLeft - strip.offsetLeft;
      if (left < strip.scrollLeft) strip.scrollLeft = left;
      else if (left + dot.offsetWidth > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = left + dot.offsetWidth - strip.clientWidth;
    };
    const render = (next, animate = true) => {
      if (!phone.matches) return;
      finishWrap();
      const previous = current;
      current = next;
      hydrateImage(items[current]);
      items.forEach((item, index) => {
        item.hidden = false;
        item.inert = index !== current;
        item.setAttribute('aria-hidden', String(index !== current));
      });
      let visual = current + 1;
      if (animate && previous !== current && !reducedMotion.matches) {
        if (previous === items.length - 1 && current === 0) {
          visual = items.length + 1;
          hydrateImage(clones[1]);
          wrapTarget = 1;
        } else if (previous === 0 && current === items.length - 1) {
          visual = 0;
          hydrateImage(clones[0]);
          wrapTarget = items.length;
        }
        track.style.setProperty('--mp-gallery-index', String(visual));
        if (wrapTarget !== null) wrapTimer = window.setTimeout(finishWrap, 550);
      } else instant(visual);
      revealDot();
      warmNextImage();
    };
    const applyViewport = () => {
      if (phone.matches) {
        if (!clones.length) {
          [items[items.length - 1], items[0]].forEach((item, index) => {
            const clone = item.cloneNode(true);
            clone.removeAttribute('data-mp-media-id');
            clone.classList.add('mp-gallery__loop-clone');
            clone.setAttribute('aria-hidden', 'true');
            clone.inert = true;
            clone.hidden = false;
            clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
            clone.querySelectorAll('video').forEach(video => { video.removeAttribute('autoplay'); video.preload = 'none'; });
            if (index === 0) track.prepend(clone);
            else track.append(clone);
            clones.push(clone);
          });
        }
        gallery.dataset.mpMobileGalleryReady = 'true';
        render(current, false);
      } else {
        finishWrap();
        clones.splice(0).forEach(clone => clone.remove());
        delete gallery.dataset.mpMobileGalleryReady;
        items.forEach((item, index) => { item.hidden = index !== current; item.inert = false; });
        track.style.removeProperty('--mp-gallery-index');
        track.style.removeProperty('--mp-gallery-drag');
      }
    };
    root.addEventListener('mp:gallery-select', event => {
      const next = items.findIndex(item => item.dataset.mpMediaId === event.detail.mediaId);
      if (next < 0) return;
      if (phone.matches) render(next);
      else current = next;
    });
    const viewport = gallery.querySelector('.mp-gallery__viewport');
    viewport.addEventListener('pointerdown', event => {
      if (!phone.matches || !event.isPrimary || event.button !== 0 || event.target.closest('video, iframe, model-viewer, button')) return;
      finishWrap();
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, horizontal: false };
      viewport.setPointerCapture(event.pointerId);
    });
    viewport.addEventListener('pointermove', event => {
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.horizontal && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { drag = null; return; }
      if (!drag.horizontal && Math.abs(dx) < 8) return;
      drag.horizontal = true;
      drag.dx = dx;
      gallery.dataset.mpGalleryDragging = 'true';
      track.style.setProperty('--mp-gallery-drag', `${dx}px`);
    });
    const endDrag = event => {
      if (!drag || drag.id !== event.pointerId) return;
      const previousDrag = drag;
      drag = null;
      delete gallery.dataset.mpGalleryDragging;
      track.style.removeProperty('--mp-gallery-drag');
      if (event.type !== 'pointercancel' && previousDrag.horizontal && Math.abs(previousDrag.dx) > Math.min(50, viewport.clientWidth * .15)) {
        gallery.querySelector(previousDrag.dx < 0 ? '[data-mp-gallery-next]' : '[data-mp-gallery-previous]')?.click();
      }
    };
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('dragstart', event => { if (phone.matches) event.preventDefault(); });
    phone.addEventListener('change', applyViewport);
    applyViewport();
  };

  const initDetails = (root) => {
    const nav = root.querySelector('[data-mp-mobile-detail-nav]');
    const dialog = root.querySelector('.mp-mobile-detail-dialog');
    if (!nav || !dialog?.showModal || nav.dataset.mpMobileDetailBound) return;
    nav.dataset.mpMobileDetailBound = 'true';
    const entries = [...nav.querySelectorAll('[data-mp-mobile-detail-open]')];
    entries.forEach(entry => entry.setAttribute('aria-expanded', 'false'));
    const body = dialog.querySelector('[data-mp-mobile-detail-body]');
    const panel = dialog.querySelector('.mp-mobile-detail-dialog__panel');
    const closeButton = dialog.querySelector('[data-mp-mobile-detail-close]');
    const heading = dialog.querySelector('h2');
    const sources = new Map();
    const add = (kind, node) => {
      if (!node || [...sources.values()].flat().some(source => source.node === node)) return;
      const anchor = document.createComment(`mp-mobile-${kind}-position`);
      node.before(anchor);
      node.dataset.mpMobileDetailSource = kind;
      const group = sources.get(kind) || [];
      group.push({ node, anchor, hidden: node.hidden });
      sources.set(kind, group);
    };
    add('description', root.querySelector('.mp-description'));
    document.querySelectorAll('[data-mp-product-content] .mp-story').forEach(node => add('description', node));
    document.querySelectorAll('[data-mp-product-content] .mp-manuals:not(.mp-specs)').forEach(node => add('downloads', node));
    const calculator = document.querySelector('mp-product-savings, [data-mp-yield-section]');
    if (calculator) add('calculator', calculator.closest('.mp-decision') || calculator);
    add('dealer', nav.querySelector('[data-mp-mobile-dealer-content]'));
    document.body.append(dialog);
    let activeKind;
    let returnFocus;
    let savedScroll;
    let savedOverflow;
    let animation;
    let closing = false;
    const downloadOriginals = new Map();
    const enhanceDownloads = () => {
      body.querySelectorAll('.mp-download').forEach(link => {
        if (downloadOriginals.has(link)) return;
        const label = link.querySelector('strong')?.textContent.trim() || link.textContent.trim();
        let filename = new URL(link.href, window.location.href).pathname.split('/').pop() || '';
        try { filename = decodeURIComponent(filename); } catch (_) { /* Retain the original filename. */ }
        const types = [
          [/Installations- und Bedienungsanleitung/i, 'Installations- und Bedienungsanleitung'],
          [/Kurzanleitung|Quick Start/i, 'Kurzanleitung'],
          [/Bedienungsanleitung|Benutzerhandbuch|Betriebsanleitung|User Manual/i, 'Bedienungsanleitung'],
          [/Installationsanleitung|Installation Manual/i, 'Installationsanleitung'],
          [/Datenblatt|datasheet/i, 'Datenblatt'],
          [/Sicherheitshinweise|Sicherheitsinformationen|Safety/i, 'Sicherheitshinweise'],
          [/RoHS/i, 'RoHS-Zertifikat'],
          [/\bTOR\b|\bOVE\b/i, 'TOR/OVE-Zertifikat'],
          [/Konformitätserklärung|Declaration of Conformity/i, 'Konformitätserklärung'],
          [/Garantiebedingungen|warranty/i, 'Garantiebedingungen'],
          [/Produktbroschüre|Broschüre/i, 'Produktbroschüre'],
        ];
        const type = types.find(([pattern]) => pattern.test(label))?.[1] || label;
        const declaration = `${label} ${filename}`.replace(/[_-]/g, ' ');
        const de = /\bDE\b|\bDeutsch\b|\bGerman\b/i.test(declaration);
        const en = /\bEN\b|\bEnglisch\b|\bEnglish\b/i.test(declaration);
        const language = de && en ? 'DE/EN' : de ? 'DE' : en ? 'EN' : /\bMULTI\b|mehrsprachig/i.test(declaration) ? 'MULTI' : '';
        downloadOriginals.set(link, { nodes: [...link.childNodes], ariaLabel: link.getAttribute('aria-label') });
        const copy = document.createElement('span');
        copy.className = 'mp-download__copy';
        const title = document.createElement('strong');
        title.textContent = type;
        const file = document.createElement('span');
        file.className = 'mp-download__filename';
        file.textContent = filename;
        file.title = filename;
        copy.append(title, file);
        const action = document.createElement('span');
        action.className = 'mp-download__action';
        action.setAttribute('aria-hidden', 'true');
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M12 3v16m-6-6 6 6 6-6');
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke', 'currentColor');
        path.setAttribute('stroke-width', '1.6');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        svg.append(path);
        action.append(svg);
        link.replaceChildren(copy);
        if (language) {
          const badge = document.createElement('span');
          badge.className = 'mp-download__language';
          badge.textContent = language;
          link.append(badge);
        }
        link.append(action);
        link.setAttribute('aria-label', label);
        link.dataset.mpMobileDownload = 'true';
      });
    };
    const restoreContent = () => {
      if (!activeKind) return;
      downloadOriginals.forEach(({ nodes, ariaLabel }, link) => {
        link.replaceChildren(...nodes);
        if (ariaLabel === null) link.removeAttribute('aria-label');
        else link.setAttribute('aria-label', ariaLabel);
        delete link.dataset.mpMobileDownload;
      });
      downloadOriginals.clear();
      (sources.get(activeKind) || []).forEach(({ node, anchor, hidden }) => {
        anchor.after(node);
        node.hidden = hidden;
      });
      activeKind = null;
    };
    const loadFallback = kind => {
      if (sources.has(kind)) return;
      const node = document.createElement('div');
      node.hidden = true;
      if (kind === 'calculator') {
        const template = root.querySelector('[data-mp-mobile-calculator-fallback]');
        if (!template) return;
        node.append(template.content.cloneNode(true));
        const scripts = [...node.querySelectorAll('script[src]')];
        scripts.forEach(script => {
          const src = script.src;
          script.remove();
          if ([...document.scripts].some(existing => existing.src === src)) return;
          const loader = document.createElement('script');
          loader.src = src;
          loader.defer = true;
          document.head.append(loader);
        });
      } else if (kind === 'downloads') {
        const link = document.createElement('a');
        link.href = '/pages/bedienungsanleitung';
        link.textContent = document.documentElement.lang.startsWith('de') ? 'Bedienungsanleitungen ansehen →' : 'View manuals →';
        node.append(link);
      } else return;
      nav.append(node);
      add(kind, node);
    };
    const open = (kind, trigger) => {
      if (!phone.matches || closing) return;
      loadFallback(kind);
      if (!sources.has(kind)) return;
      if (!dialog.open) {
        returnFocus = trigger;
        savedScroll = { left: window.scrollX, top: window.scrollY };
        savedOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
      }
      restoreContent();
      activeKind = kind;
      heading.textContent = entries.find(entry => entry.dataset.mpMobileDetailOpen === kind)?.querySelector('.mp-mobile-details__title').textContent || '';
      sources.get(kind).forEach(({ node }) => { node.hidden = false; body.append(node); });
      if (kind === 'calculator') document.dispatchEvent(new CustomEvent('mp:savings-load'));
      if (kind === 'downloads') enhanceDownloads();
      body.scrollTop = 0;
      if (!dialog.open) {
        dialog.showModal();
        animation = panel.animate([{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }], { duration: reducedMotion.matches ? 0 : 450, easing: 'cubic-bezier(.3,0,.15,1)' });
      }
      entries.forEach(entry => entry.setAttribute('aria-expanded', String(entry.dataset.mpMobileDetailOpen === kind)));
      closeButton.focus({ preventScroll: true });
    };
    const close = (immediate = false) => {
      if (!dialog.open || closing) return;
      closing = true;
      animation?.cancel();
      animation = panel.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(100%)' }], { duration: immediate || reducedMotion.matches ? 0 : 450, easing: 'cubic-bezier(.3,0,.15,1)' });
      animation.finished.then(() => dialog.close()).catch(() => dialog.close());
    };
    closeButton.addEventListener('click', () => close());
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab' || !dialog.open || event.target.closest('dialog') !== dialog) return;
      const focusable = [...dialog.querySelectorAll('a[href],button,input,select,textarea,[tabindex]')]
        .filter(node => node.tabIndex >= 0 && !node.disabled && !node.closest('[inert],[hidden]') && node.getClientRects().length);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if ((event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      }
    });
    dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
    dialog.addEventListener('click', event => {
      if (event.target === dialog) close();
    });
    dialog.addEventListener('close', () => {
      animation?.cancel();
      restoreContent();
      document.documentElement.style.overflow = savedOverflow || '';
      entries.forEach(entry => entry.setAttribute('aria-expanded', 'false'));
      closing = false;
      if (savedScroll) window.scrollTo({ ...savedScroll, behavior: 'instant' });
      returnFocus?.focus({ preventScroll: true });
    });
    document.addEventListener('click', event => {
      const entry = event.target.closest?.('[data-mp-mobile-detail-open]');
      if (entry && nav.contains(entry) && phone.matches) {
        event.preventDefault();
        open(entry.dataset.mpMobileDetailOpen, entry);
        return;
      }
      const link = event.target.closest?.('a[href^="#mp-product-"]');
      if (!link || !phone.matches || dialog.contains(link)) return;
      const kind = { '#mp-product-description': 'description', '#mp-product-story': 'description', '#mp-product-savings': 'calculator', '#mp-product-technical': 'downloads' }[link.getAttribute('href')];
      if (!kind) return;
      event.preventDefault();
      open(kind, link);
    }, true);
    phone.addEventListener('change', () => { if (!phone.matches) close(true); });
    if (!sources.has('description')) entries.find(entry => entry.dataset.mpMobileDetailOpen === 'description')?.setAttribute('hidden', '');
    document.body.classList.add('mp-mobile-details-ready');
  };

  const init = () => document.querySelectorAll('[data-mp-product-root]').forEach(root => { initGallery(root); initDetails(root); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
